import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, SortDirections, WafflesPaginationProps, numberSort, wafflesKeyColumnName } from "@app/shared/waffles";
import { Dispatch, JSX, SetStateAction } from "react";

// These aren't all in every table, but the extras don't hurt anything either
export const sharedEcdDrillInColumnConfig: ColumnConfig = {
  appealed: {
    displayText: "Appealed",
    testId: "appealed",
  },
  approved: {
    displayText: "Approved",
    testId: "approved",
  },
  closedGaps: {
    displayText: "Closed Gaps",
    testId: "closed-gaps",
  },
  compositeFillableGaps: {
    displayText: "Composite Fillable Gaps",
    testId: "composite-fillable-gaps",
  },
  denied: {
    displayText: "Denied",
    testId: "denied",
  },
  fillableBySingleProvider: {
    displayText: "Gaps Fillable by Single Provider",
    testId: "gaps-fillable-by-single-provider",
  },
  newGaps: {
    displayText: "New Gaps",
    testId: "new-gaps",
  },
  overallGaps: {
    displayText: "Overall Gaps",
    testId: "overall-gaps",
  },
  pending: {
    displayText: "Pending",
    testId: "pending",
  },
  projectName: {
    displayText: "Project Name",
    testId: "project-name",
  },
  submitted: {
    displayText: "Submitted",
    testId: "submitted",
  },
  unfillableGaps: {
    displayText: "Unfillable Gaps",
    testId: "unfillable-gaps",
  },
};

export const sharedEcdExportDrillInColumnConfig: ColumnConfig = {
  appealed: {
    displayText: "Appealed",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "appealed",
  },
  approved: {
    displayText: "Approved",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "approved",
  },
  closedGaps: {
    displayText: "Closed Gaps",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "closed-gaps",
  },
  compositeFillableGaps: {
    displayText: "Composite Fillable Gaps",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "composite-fillable-gaps",
  },
  denied: {
    displayText: "Denied",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "denied",
  },
  fillableBySingleProvider: {
    displayText: "Gaps Fillable by Single Provider",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "gaps-fillable-by-single-provider",
  },
  newGaps: {
    displayText: "New Gaps",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "new-gaps",
  },
  overallGaps: {
    displayText: "Overall Gaps",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "overall-gaps",
  },
  pending: {
    displayText: "Pending",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "pending",
  },
  projectName: {
    displayText: "Project Name",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "project-name",
  },
  submitted: {
    displayText: "Submitted",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "submitted",
  },
  unfillableGaps: {
    displayText: "Unfillable Gaps",
    noSort: true,
    style: { fontSize: "10px" },
    testId: "unfillable-gaps",
  },
};

export const sharedEcdDrillInCustomSort = <T = SharedEcdDrillInTableData>(
  data: T[],
  column: keyof T,
  direction: SortDirections,
  defaultColumn: keyof T,
  defaultDirection: SortDirections,
): T[] => {
  // custom default
  if (direction === SortDirections.None) {
    column = defaultColumn;
    direction = defaultDirection;
  }

  const multiplier = direction === SortDirections.Asc ? 1 : -1;
  let newData = [...data];
  switch (column) {
    case "projectName":
      newData = newData.sort(
        (a,
          b) =>
          ((((a as { projectName: JSX.Element }).projectName).key as string).toLowerCase()
            > ((b as { projectName: JSX.Element }).projectName.key as string).toLowerCase()
            ? 1 : -1) * multiplier
      );
      break;
    case "appealed":
    case "approved":
    case "closedGaps":
    case "compositeFillableGaps":
    case "denied":
    case "fillableBySingleProvider":
    case "newGaps":
    case "overallGaps":
    case "pending":
    case "submitted":
    case "unfillableGaps":
      newData = numberSort(newData, column, multiplier);
      break;
  }

  return newData;
}

export const sharedEcdDrillInPaginationProps = <T = SharedEcdDrillInTableData>(
  skip: number,
  take: number,
  setSkip: Dispatch<SetStateAction<number>>,
  data: T[],
  filterString: string,
): WafflesPaginationProps => ({
  changePage: (pageNumber: number) => {
    setSkip((pageNumber - 1) * take);
  },
  recordsPerPage: take,
  skip: skip,
  totalRecords: data.filter((d) => ((d as { projectName: JSX.Element }).projectName.key as string).toLowerCase().includes(filterString.toLowerCase())).length,
});

export const sharedEcdDrillInSlicedTableData = <T = SharedEcdDrillInTableData>(
  data: T[],
  filterString: string,
  skip: number,
  take: number,
): T[] => {
  return data
    .filter((d) => ((d as { projectName: JSX.Element }).projectName.key as string).toLowerCase().includes(filterString.toLowerCase()))
    .slice(skip, skip + take) as T[];
}

export interface SharedEcdDrillInTableData {
  [wafflesKeyColumnName]: string;
  projectName: JSX.Element;
  compositeFillableGaps: string;
  unfillableGaps: string;
  overallGaps: string;
}

export const tileFlyoutLink = (
  bodyComponent: JSX.Element,
  bodyTitle: string,
  disabled: boolean,
  testId: string,
  title: string,
): JSX.Element => {
  return (
    <FlyoutLink
      content={{
        body: bodyComponent,
        title: bodyTitle,
      }}
      classNames="qes-button-secondary p-0 mb-0"
      testId={testId}
      size="60"
      disabled={disabled}
    >
      {title}
    </FlyoutLink>
  );
}